/**
 * M94 — Stage 1 Grasslands tutorial (1-1…1-15).
 * Deterministic authored content via helpers — not random.
 */

import type { StageDefinition, StageWaveSpec } from '../../combat/campaign/types'
import type { StageFirstClearRewards } from '../../combat/campaign/firstClear'
import type { DifficultyId, EnemyRank } from '../../combat/types/primitives'
import { g, w, bossWaveGroups, overlapGroups } from './waveBuilders'

const ALL_DIFFICULTIES: DifficultyId[] = ['easy', 'normal', 'hard', 'insane']

const TOWERS = {
  fire: ['fire'],
  frost: ['fire', 'frost'],
  cannon: ['fire', 'frost', 'cannon'],
  poison: ['fire', 'frost', 'cannon', 'poison'],
  aa: ['fire', 'frost', 'cannon', 'poison', 'aa'],
  all: ['fire', 'frost', 'cannon', 'poison', 'aa', 'mage']
} as const

function rewards(ordinal: number): StageFirstClearRewards {
  const base = 5 + ordinal * 2
  return {
    easy: base,
    normal: base * 2,
    hard: base * 4,
    insane: base * 8
  }
}

/** Progressive map ladder — biome shared, geometry varies. */
const STAGE1_MAPS: Record<number, string> = {
  1: 'valley-v',
  2: 'valley-v',
  3: 'crossroads-c',
  4: 'valley-v',
  5: 'crossroads-c',
  6: 'switchback-s',
  7: 'skew',
  8: 'crossroads-c',
  9: 'twinspire',
  10: 'confluence',
  11: 'rift-r',
  12: 'switchback-s',
  13: 'skew',
  14: 'twinspire',
  15: 'confluence'
}

const STAGE1_TOWERS: Record<number, readonly string[]> = {
  1: TOWERS.fire,
  2: TOWERS.frost,
  3: TOWERS.frost,
  4: TOWERS.cannon,
  5: TOWERS.poison,
  6: TOWERS.poison,
  7: TOWERS.aa,
  8: TOWERS.all,
  9: TOWERS.all,
  10: TOWERS.all,
  11: TOWERS.all,
  12: TOWERS.all,
  13: TOWERS.all,
  14: TOWERS.all,
  15: TOWERS.all
}

/** Target wave counts — short early, longer late tutorial. */
export function stage1WaveCount(ordinal: number): number {
  if (ordinal <= 2) return 5
  if (ordinal <= 4) return 6
  if (ordinal === 5) return 8
  if (ordinal <= 7) return 7
  if (ordinal <= 9) return 8
  if (ordinal === 10) return 10
  if (ordinal <= 12) return 9
  if (ordinal <= 14) return 10
  return 12
}

function waveReward(ordinal: number, waveIndex: number, waveCount: number): number {
  const base = 35 + ordinal * 3
  const progress = waveIndex / waveCount
  return Math.round(base + progress * 40)
}

function enemyLevel(ordinal: number, waveIndex: number): number {
  return Math.max(1, Math.min(8, 1 + Math.floor((ordinal - 1) / 2) + Math.floor((waveIndex - 1) / 3)))
}

function composeWave(
  ordinal: number,
  waveIndex: number,
  waveCount: number,
  allowAir: boolean
): StageWaveSpec {
  const id = `c1s${ordinal}-w${waveIndex}`
  const reward = waveReward(ordinal, waveIndex, waveCount)
  const lv = enemyLevel(ordinal, waveIndex)
  const progress = waveIndex / waveCount
  const isBossWave = ordinal % 5 === 0 && waveIndex === waveCount
  const isElite = waveIndex >= waveCount - 1 && ordinal >= 8

  if (isBossWave) {
    const bossId = allowAir && ordinal >= 15 ? 'boss' : 'boss'
    const support = allowAir
      ? [
          g('flyer', lv, 3, 0.8, 2.0, 'NORMAL', 'ground-a'),
          g('normal', lv, 4, 0.7, 3.0)
        ]
      : [g('normal', lv, 5, 0.7, 2.0), g('fast', lv, 3, 0.55, 3.0)]
    return w(id, reward + 80, bossWaveGroups(bossId, Math.max(lv, 4), support, 1.2))
  }

  // Teaching arcs by wave band
  if (progress <= 0.25) {
    return w(id, reward, [g('normal', lv, 4 + Math.floor(ordinal / 4), 0.8, 0.35)])
  }
  if (progress <= 0.45) {
    return w(id, reward, overlapGroups([
      g('normal', lv, 3, 0.75, 0.3),
      g('fast', lv, 3 + (ordinal > 6 ? 1 : 0), 0.55, 0.3)
    ]))
  }
  if (progress <= 0.65) {
    if (ordinal >= 4) {
      return w(id, reward, [
        g('tank', lv, 1 + (ordinal >= 10 ? 1 : 0), 1.5, 0.35),
        g('normal', lv, 4, 0.7, 0.9)
      ])
    }
    return w(id, reward, [g('fast', lv, 5, 0.55, 0.3), g('normal', lv, 3, 0.75, 1.0)])
  }
  if (progress <= 0.85) {
    if (allowAir && waveIndex % 2 === 0) {
      return w(id, reward, [
        g('normal', lv, 4, 0.7, 0.3),
        g('flyer', lv, 3 + (ordinal >= 12 ? 1 : 0), 0.7, 0.5, 'NORMAL', 'ground-a')
      ])
    }
    if (ordinal >= 6) {
      return w(id, reward, [
        g('regen', lv, 2, 1.3, 0.3),
        g('fast', lv, 4, 0.55, 0.9)
      ])
    }
    return w(id, reward, [g('shield', lv, 1, 1.4, 0.3), g('normal', lv, 4, 0.7, 0.9)])
  }

  // Late pressure
  const rank: EnemyRank = isElite ? 'ELITE' : 'NORMAL'
  if (allowAir) {
    return w(id, reward, [
      g('tank', lv, 1, 1.4, 0.3, rank),
      g('flyer', lv, 3, 0.65, 0.7, 'NORMAL', 'ground-a'),
      g('fast', lv, 3, 0.5, 1.2)
    ])
  }
  if (ordinal >= 6) {
    return w(id, reward, [
      g('tank', lv, 1, 1.45, 0.3, rank),
      g('regen', lv, 2, 1.2, 0.8),
      g('fast', lv, 4, 0.5, 1.3)
    ])
  }
  return w(id, reward, [
    g('tank', lv, 1, 1.5, 0.35),
    g('normal', lv, 5, 0.65, 0.9),
    g('fast', lv, 3, 0.55, 1.4)
  ])
}

function buildStage(ordinal: number): StageDefinition {
  const waveCount = stage1WaveCount(ordinal)
  const allowAir = ordinal >= 7
  const waves: StageWaveSpec[] = []
  for (let i = 1; i <= waveCount; i += 1) {
    waves.push(composeWave(ordinal, i, waveCount, allowAir))
  }
  // Dual-lane touches for multi-route teaching maps
  if (ordinal === 7 || ordinal === 9 || ordinal === 13 || ordinal === 14) {
    const mid = waves[Math.floor(waveCount / 2)]!
    waves[Math.floor(waveCount / 2)] = {
      ...mid,
      groups: [
        g('normal', enemyLevel(ordinal, mid.groups[0]?.enemyLevel ?? 3), 3, 0.75, 0.3, 'NORMAL', 'ground-a'),
        g('normal', enemyLevel(ordinal, mid.groups[0]?.enemyLevel ?? 3), 3, 0.75, 0.35, 'NORMAL', 'ground-b'),
        ...(allowAir
          ? [g('flyer', enemyLevel(ordinal, 4), 3, 0.7, 0.6, 'NORMAL', 'ground-a')]
          : [])
      ]
    }
  }

  return {
    id: `chapter1-stage${ordinal}`,
    chapterId: 'chapter-1',
    displayLabel: `1-${ordinal}`,
    mapId: STAGE1_MAPS[ordinal]!,
    allowedTowerIds: [...STAGE1_TOWERS[ordinal]!],
    availableDifficulties: ALL_DIFFICULTIES,
    firstClearRewards: rewards(ordinal),
    waves,
    bossLevel: ordinal % 5 === 0
  }
}

export const STAGE1_TUTORIAL_STAGES: StageDefinition[] = Array.from(
  { length: 15 },
  (_, i) => buildStage(i + 1)
)

export const STAGE1_STAGE_IDS = STAGE1_TUTORIAL_STAGES.map((s) => s.id)
